import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stepper,
    Step,
    StepLabel,
    Box,
} from "@mui/material";
import { useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import Step01 from "./Step01";
import Step02 from "./Step02";
import Step03 from "./Step03";
import Step04 from "./Step04";
import { IDataSetores } from "../../../../../types/api/estoque/pesquisar-setores";
import { IDataDepartamentosEstoque } from "../../../../../types/api/estoque/pesquisar-departamentos-estoque";
import { IDataAgrotoxico } from "../../../../../types/api/geral/pesquisar-agrotoxico";
import { useMutation, useQueryClient } from "react-query";
import axiosClient from "../../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import { IAddProdutoEstoque } from "../../../../../types/mutations/estoque/adicionar-produto";
import { IDataAnpGrupo } from "../../../../../types/api/estoque/pesquisar-anp-grupo";
import { IUnidadeMedida } from "../../../../../types/api/estoque/pesquisar-unidade-medida";
import { IDataLojas } from "../../../../../types/api/geral/pesquisar-lojas";
import { CategoriaHierarquica } from "../ModalDetalheProdutoContext/DialogDetalheProduto/AdicionarCategoriaModal";

export default function DialogCadastrarProduto({
    open,
    handleClose
}: IDialogCadastrarProduto) {
    const queryClient = useQueryClient();

    const [activeStep, setActiveStep] = useState(0);

    const methods = useForm<IProdutoFormData>({
        defaultValues,
        mode: 'onChange'
    });

    const {
        handleSubmit,
        trigger,
        reset
    } = methods;

    const { mutate: criarProduto, isLoading: isLoadingSalvarProduto } = useMutation({
        mutationKey: 'salvar-rascunho',
        mutationFn: (inputs: IAddProdutoEstoque) => axiosClient.post("/estoque/produtos/adicionar-produto", inputs),
        onError: (error) => {
            console.error(error)
            queryClient.invalidateQueries("listar-todos-produtos")
            enqueueSnackbar('Erro ao criar o produto!', { variant: 'error' });
        },
        onSuccess: () => {
            queryClient.invalidateQueries("listar-todos-produtos")
            enqueueSnackbar(`Produto criado com sucesso!`, { variant: 'success' });
            handleCloseDialog();
        }
    })

    const handleNext = async () => {
        const fieldsToValidate = getFieldsForStep(activeStep);
        const isStepValid = await trigger(fieldsToValidate);

        if (isStepValid) {
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
        }
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleCloseDialog = () => {
        reset();
        setActiveStep(0);
        handleClose();
    };

    const onSubmit = (data: IProdutoFormData) => {

        const atributo = data.informacoesBasicas.atributos ?? [];
        criarProduto({
            nome: data.informacoesBasicas.nome,
            codigo_barra: data.informacoesBasicas.codigo_barra,
            departamento_id: data.informacoesBasicas.departamento?.id ?? 0,
            setor_id: data.informacoesBasicas.setor?.id ?? 0,
            descricao: data.informacoesBasicas.descricao ?? undefined,
            categoria_id: data.informacoesBasicas.categoria_id ?? 0,
            categoria_nome: data.informacoesBasicas.categoria_nome ?? '',
            atributos: atributo,
            tipo: data.especificacoes.tipo as IAddProdutoEstoque["tipo"],
            unidade_medida_sigla: data.especificacoes?.unidade_medida?.sigla ?? "",
            permiteDecimais: data.especificacoes.permiteDecimais as IAddProdutoEstoque["permiteDecimais"],
            unidadeEtiqueta: data.especificacoes.unidadePadraoEtiqueta?.sigla || "",
            quantidadeEtiqueta: data.especificacoes.quantidadePadraoEtiqueta !== '' ? Number(data.especificacoes.quantidadePadraoEtiqueta) : undefined,
            unidadeConversao: data.especificacoes.unidadeFatorConversao?.sigla || "",
            quantidadeConversao: data.especificacoes.quantidadeFatorConversao !== '' ? Number(data.especificacoes.quantidadeFatorConversao) : undefined,

            financiamentoId: data.precosCustos.financiamento !== '' ? Number(data.precosCustos.financiamento) : undefined,
            precoAutomatico: data.precosCustos.atualizaPrecoAutomatico as IAddProdutoEstoque["precoAutomatico"],
            precoQuatroDecimais: data.precosCustos.precoVenda4Decimais as IAddProdutoEstoque["precoQuatroDecimais"],
            variacaoCompra: data.precosCustos.percentualVariacaoCompra !== '' ? Number(data.precosCustos.percentualVariacaoCompra) : undefined,
            exigeValorTotalNF: data.precosCustos.pedeValorTotalNF as IAddProdutoEstoque["exigeValorTotalNF"],
            estornoICMS: data.precosCustos.estornoCreditoICMS as IAddProdutoEstoque["estornoICMS"],
            geraLMC: data.precosCustos.geraLMC as IAddProdutoEstoque["geraLMC"],
            grupoANP_id: data.precosCustos.grupoANP?.id,
            emiteReceitaAgricola: data.precosCustos.emiteReceitaAgropecuaria as IAddProdutoEstoque["emiteReceitaAgricola"],
            registroMinAgricultura_id: data.precosCustos.registroMinisterioAgricultura?.id,
            classificacaoOrigem: data.precosCustos.classificacaoOrigem as IAddProdutoEstoque["classificacaoOrigem"],

            permiteComprar: data.dadosAdicionais.pedeComprar as IAddProdutoEstoque["permiteComprar"],
            desativado: data.dadosAdicionais.desativado as IAddProdutoEstoque["desativado"],
            produtoFormula: data.dadosAdicionais.produtoFormula as IAddProdutoEstoque["produtoFormula"],
            permiteRessuprimento: data.dadosAdicionais.ressupre as IAddProdutoEstoque["permiteRessuprimento"],
            centroDistribuicaoId: data.dadosAdicionais?.lojaCentroDistribuicao?.id ?? 0,
            codigoAnterior: data.dadosAdicionais.codigoAnterior !== '' ? Number(data.dadosAdicionais.codigoAnterior) : undefined,
            tipoItem: data.dadosAdicionais.tipoItem !== '' ? Number(data.dadosAdicionais.tipoItem) : 0,
            familiaId: data.dadosAdicionais.familia !== '' ? Number(data.dadosAdicionais.familia) : undefined,
            tipoProdutoId: data.dadosAdicionais.tipoProduto !== '' ? Number(data.dadosAdicionais.tipoProduto) : undefined,
        });
    };

    const isLastStep = activeStep === steps.length - 1;

    return (
        <Dialog
            open={open}
            maxWidth="md"
            fullWidth
        >
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogTitle>
                        Cadastrar Produto
                    </DialogTitle>

                    <DialogContent>
                        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>

                        <Box sx={{ minHeight: 400 }}>
                            {getStepContent(activeStep)}
                        </Box>
                    </DialogContent>

                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button
                            variant="text"
                            onClick={handleCloseDialog}
                        >
                            Cancelar
                        </Button>

                        <Box sx={{ flex: '1 1 auto' }} />

                        <Button
                            disabled={activeStep === 0}
                            onClick={handleBack}
                            variant="outlined"
                            sx={{ mr: 1 }}
                        >
                            Anterior
                        </Button>

                        {
                            isLastStep && (
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={isLoadingSalvarProduto}
                                >
                                    {isLoadingSalvarProduto ? "Enviando..." : "Finalizar"}
                                </Button>
                            )
                        }
                        {
                            !isLastStep && (
                                <Button
                                    variant="contained"
                                    onClick={handleNext}
                                    color="primary"
                                >
                                    Próximo
                                </Button>
                            )
                        }
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
}

const steps = [
    'Informações Básicas',
    'Especificações',
    'Preços e Custos',
    'Dados adicionais'
];

const defaultValues: IProdutoFormData = {
    informacoesBasicas: {
        nome: '',
        descricao: '',
        categoria_nome: '',
        departamento: null,
        setor: null,
        codigo_barra: '',
        categoria_id: null,
        atributos: {},
        categoria_data: null
    },
    especificacoes: {
        tipo: '',
        unidade_medida: null,
        permiteDecimais: '',
        unidadePadraoEtiqueta: null,
        quantidadePadraoEtiqueta: '',
        unidadeFatorConversao: null,
        quantidadeFatorConversao: ''
    },
    precosCustos: {
        financiamento: '',
        atualizaPrecoAutomatico: '',
        precoVenda4Decimais: '',
        percentualVariacaoCompra: '',
        pedeValorTotalNF: '',
        estornoCreditoICMS: '',
        geraLMC: '',
        grupoANP: null,
        emiteReceitaAgropecuaria: '',
        registroMinisterioAgricultura: null,
        classificacaoOrigem: ''
    },
    dadosAdicionais: {
        pedeComprar: '',
        desativado: '',
        produtoFormula: '',
        ressupre: '',
        lojaCentroDistribuicao: null,
        codigoAnterior: '',
        tipoItem: '',
        familia: '',
        tipoProduto: ''
    }
};

function getStepContent(step: number) {
    switch (step) {
        case 0:
            return <Step01 />;
        case 1:
            return <Step02 />;
        case 2:
            return <Step03 />;
        case 3:
            return <Step04 />;
        default:
            return 'Passo desconhecido';
    }
}

function getFieldsForStep(step: number) {
    switch (step) {
        case 0:
            return [
                'informacoesBasicas.nome',
                'informacoesBasicas.codigo_barra',
                'informacoesBasicas.categoria_nome',
                'informacoesBasicas.categoria_id',
                'informacoesBasicas.atributos',
                'informacoesBasicas.setor',
                'informacoesBasicas.departamento',
            ] as const;
        case 1:
            return [
                'especificacoes.tipo',
                'especificacoes.unidade_medida',
                'especificacoes.permiteDecimais',
                'especificacoes.unidadePadraoEtiqueta',
                'especificacoes.quantidadePadraoEtiqueta',
                'especificacoes.unidadeFatorConversao',
                'especificacoes.quantidadeFatorConversao',
            ] as const;
        case 2:
            return [
                'precosCustos.financiamento',
                'precosCustos.atualizaPrecoAutomatico',
                'precosCustos.precoVenda4Decimais',
                'precosCustos.percentualVariacaoCompra',
                'precosCustos.pedeValorTotalNF',
                'precosCustos.estornoCreditoICMS',
                'precosCustos.geraLMC',
                'precosCustos.grupoANP',
                'precosCustos.emiteReceitaAgropecuaria',
                'precosCustos.registroMinisterioAgricultura',
                'precosCustos.classificacaoOrigem'
            ] as const;
        case 3:
            return [
                'dadosAdicionais.pedeComprar',
                'dadosAdicionais.desativado',
                'dadosAdicionais.produtoFormula',
                'dadosAdicionais.ressupre',
                'dadosAdicionais.lojaCentroDistribuicao',
                'dadosAdicionais.codigoAnterior',
                'dadosAdicionais.tipoItem',
                'dadosAdicionais.familia',
                'dadosAdicionais.tipoProduto'
            ] as const;
        default:
            return [] as const;
    }
}

interface IDialogCadastrarProduto {
    open: boolean;
    handleClose: () => void;
}

export interface IProdutoFormData {
    informacoesBasicas: {
        nome: string;
        descricao: string;
        categoria_nome: string;
        departamento: IDataDepartamentosEstoque | null;
        setor: IDataSetores | null;
        codigo_barra: string;
        categoria_id: number | null;
        atributos: Record<string, string>;
        categoria_data: CategoriaHierarquica | null;
    };
    especificacoes: {
        tipo: string;
        unidade_medida: IUnidadeMedida | null;
        permiteDecimais: string;
        unidadePadraoEtiqueta: IUnidadeMedida | null;
        quantidadePadraoEtiqueta: number | '';
        unidadeFatorConversao: IUnidadeMedida | null;
        quantidadeFatorConversao: number | '';
    };
    precosCustos: {
        financiamento: number | '';
        atualizaPrecoAutomatico: string;
        precoVenda4Decimais: string;
        percentualVariacaoCompra: number | '';
        pedeValorTotalNF: string;
        estornoCreditoICMS: string;
        geraLMC: string;
        grupoANP: IDataAnpGrupo | null;
        emiteReceitaAgropecuaria: string;
        registroMinisterioAgricultura: IDataAgrotoxico | null;
        classificacaoOrigem: string;
    };
    dadosAdicionais: {
        pedeComprar: string;
        desativado: string;
        produtoFormula: string;
        ressupre: string;
        lojaCentroDistribuicao: IDataLojas | null;
        codigoAnterior: number | '';
        tipoItem: number | '';
        familia: number | '';
        tipoProduto: number | '';
    };
}